import React, { useEffect, useRef, useState } from 'react';
import { STROKE_PATHS } from '../strokes-array';

/**
 * ProvansPreloader — splash-экран с написанием логотипа «Прованс».
 *
 * Поведение:
 *  1) DRAWING — анимация написания штрихами кисти (mask reveal по картинке `logoSrc`).
 *  2) MORPHING — после готовности сайта SVG-контейнер «улетает» (translate+scale)
 *     в координаты элемента `morphTargetSelector` (по умолчанию `.hero-title-image`).
 *     Параллельно cream-фон угасает, а тёмный обрезанный логотип кросс-фейдится
 *     в `morphLogoSrc` (например, белый вариант) — так финальный кадр прелоадера
 *     визуально совпадает с тем, что есть в hero-title.
 *  3) DONE — `onFinished` зовётся, прелоадер можно размонтировать; элемент
 *     hero-title уже виден на нужном месте без рывка.
 *
 *  Если `.hero-title-image` не найден (например, открыт другой роут), компонент
 *  просто плавно угасает как раньше.
 */

const LOGO_DATA_URL = typeof window !== 'undefined' ? (window.PROVANS_LOGO_URL || null) : null;

/**
 * @param {object} props
 * @param {boolean} [props.siteReady]
 * @param {() => void} [props.onFinished]
 * @param {number} [props.duration]
 * @param {number} [props.minDisplay]
 * @param {number} [props.fadeOut]
 * @param {string} [props.background]
 * @param {string | null} [props.tagline]
 * @param {string | null} [props.taglineSrc]
 * @param {number} [props.strokeWidth]
 * @param {Array<{d: string, width?: number} | string>} [props.strokePaths]
 * @param {string} [props.logoSrc]
 * @param {string} [props.morphTargetSelector]
 * @param {string} [props.morphLogoSrc]
 * @param {number} [props.morphDuration]
 */
export default function ProvansPreloader({
  siteReady = false,
  onFinished,
  duration = 2800,
  minDisplay,
  fadeOut = 600,
  background = 'cream',
  tagline = null,
  taglineSrc = null,
  strokeWidth = 130,
  strokePaths = STROKE_PATHS,
  logoSrc,
  morphTargetSelector = '.hero-title-image',
  morphLogoSrc,
  morphDuration = 900,
}) {
  const svgRef = useRef(null);
  const morphRef = useRef(null);
  const targetElRef = useRef(null);
  const [taglineVisible, setTaglineVisible] = useState(false);
  const [animDone, setAnimDone] = useState(false);
  const [phase, setPhase] = useState('drawing'); // drawing | settling | morphing | done
  const [morphStyle, setMorphStyle] = useState(null);
  const [bgFading, setBgFading] = useState(false);
  const [showMorphLogo, setShowMorphLogo] = useState(false);
  const startedAt = useRef(0);

  // Анимация написания.
  useEffect(() => {
    const svg = svgRef.current;
    const paths = strokePaths;
    if (!svg || !paths) return;
    startedAt.current = performance.now();

    const NS = 'http://www.w3.org/2000/svg';
    const group = svg.querySelector('#brushGroup');
    group.innerHTML = '';
    const els = [];
    const lens = [];
    for (const stroke of paths) {
      const d = typeof stroke === 'string' ? stroke : stroke?.d;
      const width = typeof stroke === 'object' && stroke?.width ? stroke.width : strokeWidth;
      if (!d) continue;
      const p = document.createElementNS(NS, 'path');
      p.setAttribute('d', d);
      p.setAttribute('stroke-width', String(width));
      group.appendChild(p);
      const len = p.getTotalLength();
      p.setAttribute('stroke-dasharray', String(len));
      p.setAttribute('stroke-dashoffset', String(len));
      els.push(p);
      lens.push(len);
    }

    const totalLen = lens.reduce((a, b) => a + b, 0);
    const liftMs = 100;
    const totalLifts = lens.length - 1;
    const drawDur = Math.max(800, duration - totalLifts * liftMs);

    let segIdx = 0;
    let segStart = performance.now();
    let raf;
    let timer;

    const tick = (now) => {
      const segLen = lens[segIdx];
      const segDur = drawDur * (segLen / totalLen);
      const k = Math.min(1, (now - segStart) / segDur);
      const off = lens[segIdx] * (1 - k);
      els[segIdx].setAttribute('stroke-dashoffset', off);
      if (k < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        els[segIdx].setAttribute('stroke-dashoffset', 0);
        segIdx++;
        if (segIdx >= els.length) {
          if (tagline || taglineSrc) setTimeout(() => setTaglineVisible(true), 200);
          setTimeout(() => setAnimDone(true), 400);
          return;
        }
        timer = setTimeout(() => {
          segStart = performance.now();
          raf = requestAnimationFrame(tick);
        }, liftMs);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); clearTimeout(timer); };
  }, [duration, strokeWidth, tagline, taglineSrc, strokePaths]);

  // Триггер morph (или простой fade-out, если цели нет).
  useEffect(() => {
    if (phase !== 'drawing' || !animDone || !siteReady) return;
    const minMs = minDisplay ?? (duration + 400);
    const elapsed = performance.now() - startedAt.current;
    const wait = Math.max(0, minMs - elapsed);
    const settleMs = morphLogoSrc ? 280 : 0;

    const t = setTimeout(() => {
      const source = morphRef.current;
      const target = morphTargetSelector
        ? document.querySelector(morphTargetSelector)
        : null;

      if (!source || !target) {
        // Fallback — просто fade-out.
        setBgFading(true);
        setPhase('morphing');
        setTimeout(() => {
          onFinished && onFinished();
          window.dispatchEvent(new Event('provans-preloader-done'));
        }, fadeOut);
        return;
      }

      // Сначала на месте сводим к тому же asset, что в hero — без прыжка текста.
      setPhase('settling');
      setShowMorphLogo(true);

      const runMorph = () => {
        const sourceRect = source.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();

        // Равномерный scale — тот же aspect у source и hero, без сжатия/дёрганья.
        const scale = targetRect.width / sourceRect.width;
        const tx =
          (targetRect.left + targetRect.width / 2) -
          (sourceRect.left + sourceRect.width / 2);
        const ty =
          (targetRect.top + targetRect.height / 2) -
          (sourceRect.top + sourceRect.height / 2);

        // Прячем целевой элемент на время morph, чтобы не было двойника.
        target.style.opacity = '0';
        targetElRef.current = target;

        setMorphStyle({
          transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
        });
        setBgFading(true);
        setPhase('morphing');

        setTimeout(() => {
          if (targetElRef.current) {
            // В тот же кадр, что и unmount: убираем inline-стиль —
            // hero-title сразу появляется на финальной позиции.
            targetElRef.current.style.opacity = '';
            targetElRef.current = null;
          }
          onFinished && onFinished();
          window.dispatchEvent(new Event('provans-preloader-done'));
        }, morphDuration);
      };

      if (settleMs) {
        setTimeout(runMorph, settleMs);
      } else {
        runMorph();
      }
    }, wait);

    return () => clearTimeout(t);
  }, [
    phase,
    animDone,
    siteReady,
    duration,
    minDisplay,
    fadeOut,
    morphTargetSelector,
    morphLogoSrc,
    morphDuration,
    onFinished,
  ]);

  // Подстраховка: если компонент размонтировался во время morph,
  // не оставляем целевой элемент скрытым.
  useEffect(() => {
    return () => {
      if (targetElRef.current) {
        targetElRef.current.style.opacity = '';
        targetElRef.current = null;
      }
    };
  }, []);

  const bgColor =
    background === 'ink' ? '#1c1a14' :
    background === 'white' ? '#ffffff' :
    background === 'paper' ? '#ebe2cf' :
    '#f4ecd8';

  const src = logoSrc || LOGO_DATA_URL;
  const isMorphing = phase === 'morphing';
  const isSettling = phase === 'settling';
  const hideDrawLayer = isMorphing || isSettling || showMorphLogo;
  const fadeMs = isMorphing ? morphDuration : fadeOut;
  const logoSwapMs = 280;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        pointerEvents: bgFading ? 'none' : 'auto',
      }}
    >
      {/* Фон отдельным слоем — угасает, не влияя на лого. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: bgColor,
          opacity: bgFading ? 0 : 1,
          transition: `opacity ${fadeMs}ms ease-out`,
        }}
      />

      {/* Центрирующий слой с лого и подписью. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <div
          ref={morphRef}
          className="provans-logo-box"
          style={{
            // Тот же бокс, что и .hero-title — morph без смены пропорций.
            position: 'relative',
            transformOrigin: 'center center',
            transition: isMorphing
              ? `transform ${morphDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`
              : undefined,
            willChange: 'transform',
            ...(morphStyle || {}),
          }}
        >
          {/* Слой прорисовки: wordmark + подпись (как в полном лого). */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: hideDrawLayer ? 0 : 1,
              transition: `opacity ${logoSwapMs}ms ease-out`,
            }}
          >
            <div style={{ width: '100%', aspectRatio: '3491 / 1187' }}>
              <svg
                ref={svgRef}
                viewBox="0 0 3491 1187"
                xmlns="http://www.w3.org/2000/svg"
                style={{ width: '100%', height: '100%', display: 'block' }}
                aria-label="Прованс"
              >
                <defs>
                  <mask id="provans-reveal" maskUnits="userSpaceOnUse" x="-200" y="-200" width="3891" height="1587">
                    <rect x="-200" y="-200" width="3891" height="1587" fill="black" />
                    <g id="brushGroup" fill="none" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
                  </mask>
                </defs>
                {src && (
                  <image
                    href={src}
                    x="0" y="0" width="3491" height="1187"
                    preserveAspectRatio="none"
                    mask="url(#provans-reveal)"
                  />
                )}
              </svg>
            </div>

            {taglineSrc && (
              <img
                src={taglineSrc}
                alt=""
                aria-hidden="true"
                style={{
                  display: 'block',
                  width: '48%',
                  height: 'auto',
                  marginTop: '2.5%',
                  opacity: taglineVisible ? 1 : 0,
                  transform: taglineVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 700ms ease-out, transform 700ms ease-out',
                }}
              />
            )}

            {!taglineSrc && tagline && (
              <div
                style={{
                  marginTop: 18,
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontStyle: 'italic',
                  fontSize: 'clamp(14px, 1.8vw, 22px)',
                  letterSpacing: '0.18em',
                  color: background === 'ink' ? '#c9b676' : '#8b7a3f',
                  opacity: taglineVisible ? 1 : 0,
                  transform: taglineVisible ? 'translateY(0)' : 'translateY(8px)',
                  transition: 'opacity 700ms ease-out, transform 700ms ease-out',
                }}
              >
                {tagline}
              </div>
            )}
          </div>

          {/* Финальный кадр morph = тот же asset, что в hero — без прыжка текста. */}
          {morphLogoSrc && (
            <img
              src={morphLogoSrc}
              alt=""
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                objectPosition: 'center',
                opacity: showMorphLogo ? 1 : 0,
                transition: `opacity ${logoSwapMs}ms ease-in`,
                filter: 'drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.3))',
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

if (typeof window !== 'undefined') {
  window.ProvansPreloader = ProvansPreloader;
}
